import type { TransferListItem, TransferProps } from '@ibsys/kit';
import { Checkbox } from '@ibsys/kit';
import { FC, useMemo } from 'react';

type Props = {
  items: TransferListItem[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  /** Кастомный рендер дочернего элемента группы (как в Transfer); если возвращает null — рендерится дефолтный чекбокс */
  renderItem?: TransferProps['renderItem'];
};

type GroupProps = {
  group: TransferListItem;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  renderItem?: TransferProps['renderItem'];
};

const CheckboxGroup: FC<GroupProps> = ({ group, selectedIds, onSelectionChange, renderItem }) => {
  const childIds = useMemo(() => (group.children ?? []).map((c) => String(c.id)), [group.children]);

  const selectedCount = childIds.filter((id) => selectedIds.includes(id)).length;
  const groupValue = selectedCount === childIds.length ? true : selectedCount > 0 ? 'indeterminate' : false;

  const handleGroupChange = (checked: boolean | 'indeterminate') => {
    if (checked === true) {
      const next = Array.from(new Set([...selectedIds, ...childIds]));
      onSelectionChange(next);
    } else {
      onSelectionChange(selectedIds.filter((id) => !childIds.includes(id)));
    }
  };

  const handleItemChange = (id: string, checked: boolean | 'indeterminate') => {
    if (checked === true) {
      onSelectionChange([...selectedIds, id]);
    } else {
      onSelectionChange(selectedIds.filter((sid) => sid !== id));
    }
  };

  return (
    <div>
      <div className="sticky top-0 z-10 bg-background py-2 mb-2 border-y">
        <Checkbox value={groupValue} onChange={handleGroupChange} label={group.label} />
      </div>
      <div className="flex flex-col">
        {(group.children ?? []).map((item) => {
          const id = String(item.id);
          const checked = selectedIds.includes(id);
          if (renderItem) {
            const rendered = renderItem(item, {
              checked,
              onCheckedChange: (next) => handleItemChange(id, next),
            });
            if (rendered != null) return <div key={id}>{rendered}</div>;
          }
          return (
            <Checkbox
              key={item.id}
              value={checked}
              label={item.label}
              onChange={(value) => handleItemChange(id, value)}
            />
          );
        })}
      </div>
    </div>
  );
};

export const ConversationAttributesCheckboxList: FC<Props> = ({
  items,
  selectedIds,
  onSelectionChange,
  renderItem,
}) => {
  const groups = items.filter((item) => item.type !== 'separator' && item.children?.length);

  return (
    <div className="flex flex-col overflow-auto">
      {groups.map((group) => (
        <CheckboxGroup
          key={group.id}
          group={group}
          selectedIds={selectedIds}
          onSelectionChange={onSelectionChange}
          renderItem={renderItem}
        />
      ))}
    </div>
  );
};
